import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface Product {
  name: string;
  tagline: string;
  link: string;
  external?: boolean;
  color: string;
  colorLight: string;
  colorBorder: string;
  icon: React.ReactNode;
}

const products: Product[] = [
  {
    name: 'Neurogames',
    tagline: 'Le mouvement au service du cerveau',
    link: '/programme-neurogames',
    color: '#F59E0B',
    colorLight: 'rgba(245, 158, 11, 0.1)',
    colorBorder: 'rgba(245, 158, 11, 0.2)',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
        {/* Brain with movement lines */}
        <path
          d="M20 6C14.5 6 10 10 10 15c0 3 1.5 5.5 3.8 7.2.5.4.7 1 .7 1.6V28a2 2 0 002 2h7a2 2 0 002-2v-4.2c0-.6.3-1.2.7-1.6C28.5 20.5 30 18 30 15c0-5-4.5-9-10-9z"
          stroke="#F59E0B"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20 6v18M15 12c2 2 6 2 8 0M14 18c2 1.5 8 1.5 10 0"
          stroke="#F59E0B"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Movement arcs */}
        <path
          d="M6 13c-1-3 0-6 2-8M34 13c1-3 0-6-2-8"
          stroke="#F59E0B"
          strokeWidth="1.2"
          strokeLinecap="round"
          opacity="0.5"
        />
        <path
          d="M8 17c-2-1-3-3-3-5M32 17c2-1 3-3 3-5"
          stroke="#F59E0B"
          strokeWidth="1.2"
          strokeLinecap="round"
          opacity="0.35"
        />
        {/* Base */}
        <path d="M16 32h8M17 34h6" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: 'Soft Skills',
    tagline: "L'intelligence émotionnelle cultivée",
    link: '/programme-soft-skills',
    color: '#0D9488',
    colorLight: 'rgba(13, 148, 136, 0.1)',
    colorBorder: 'rgba(13, 148, 136, 0.2)',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
        {/* Heart with people silhouettes */}
        <path
          d="M20 35s-12-7.5-12-16c0-4.5 3.5-8 7.5-8 2.5 0 4.5 1.5 4.5 1.5S22 9.5 24.5 9.5C28.5 9.5 32 13 32 17.5 32 27.5 20 35 20 35z"
          stroke="#0D9488"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Person left */}
        <circle cx="15" cy="19" r="2" stroke="#0D9488" strokeWidth="1.3" />
        <path d="M12 25.5c0-1.7 1.3-3 3-3s3 1.3 3 3" stroke="#0D9488" strokeWidth="1.3" strokeLinecap="round" />
        {/* Person right */}
        <circle cx="25" cy="19" r="2" stroke="#0D9488" strokeWidth="1.3" />
        <path d="M22 25.5c0-1.7 1.3-3 3-3s3 1.3 3 3" stroke="#0D9488" strokeWidth="1.3" strokeLinecap="round" />
        {/* Connection arc */}
        <path d="M18 17c1-2 3-2 4 0" stroke="#0D9488" strokeWidth="1" strokeLinecap="round" opacity="0.5" />
      </svg>
    ),
  },
  {
    name: 'Programme IA',
    tagline: 'Pour préparer les enfants au monde de demain',
    link: '/programme-ia',
    color: '#7C3AED',
    colorLight: 'rgba(124, 58, 237, 0.1)',
    colorBorder: 'rgba(124, 58, 237, 0.2)',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
        {/* Robot head: rounded square with antenna */}
        <path
          d="M14 14h12a3 3 0 013 3v8a3 3 0 01-3 3H14a3 3 0 01-3-3v-8a3 3 0 013-3z"
          stroke="#7C3AED"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Antenna */}
        <path d="M20 14V9" stroke="#7C3AED" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="20" cy="7.5" r="1.6" fill="#7C3AED" />
        {/* Eyes */}
        <circle cx="16.5" cy="20" r="1.4" fill="#7C3AED" />
        <circle cx="23.5" cy="20" r="1.4" fill="#7C3AED" />
        {/* Smile */}
        <path d="M17 24.5c1 1 2 1.5 3 1.5s2-.5 3-1.5" stroke="#7C3AED" strokeWidth="1.4" strokeLinecap="round" />
        {/* Side connectors */}
        <path d="M11 19h-2M11 23h-2M31 19h-2M31 23h-2" stroke="#7C3AED" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
      </svg>
    ),
  },
  {
    name: 'Lumi',
    tagline: 'Plateforme de révision',
    link: 'https://revision.gssanaa.com',
    external: true,
    color: '#00A3FF',
    colorLight: 'rgba(0, 163, 255, 0.1)',
    colorBorder: 'rgba(0, 163, 255, 0.2)',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
        {/* AI sparkle / star */}
        <path
          d="M20 4l2.5 8.5L31 15l-8.5 2.5L20 26l-2.5-8.5L9 15l8.5-2.5L20 4z"
          stroke="#00A3FF"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        {/* Small sparkles */}
        <path
          d="M31 26l1.2 3.8L36 31l-3.8 1.2L31 36l-1.2-3.8L26 31l3.8-1.2L31 26z"
          stroke="#00A3FF"
          strokeWidth="1.2"
          strokeLinejoin="round"
          opacity="0.6"
        />
        <path
          d="M9 28l1 2.5L13 32l-3 1L9 36l-1-3L5 32l3-1.5L9 28z"
          stroke="#00A3FF"
          strokeWidth="1"
          strokeLinejoin="round"
          opacity="0.4"
        />
        {/* Central dot */}
        <circle cx="20" cy="15" r="1.5" fill="#00A3FF" opacity="0.5" />
      </svg>
    ),
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const InnovationStrip = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-gray-50 via-white to-gray-50 border-y border-gray-100">
      {/* Subtle decorative background */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 12% 50%, #F59E0B 0%, transparent 50%), radial-gradient(circle at 38% 50%, #0D9488 0%, transparent 50%), radial-gradient(circle at 64% 50%, #7C3AED 0%, transparent 50%), radial-gradient(circle at 88% 50%, #00A3FF 0%, transparent 50%)',
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {products.map((product, index) => {
            const linkProps = {
              className: 'group flex items-center gap-3.5 px-4 py-3 rounded-xl transition-all duration-300 hover:shadow-md',
              style: {
                backgroundColor: 'transparent',
                borderWidth: '1px',
                borderStyle: 'solid' as const,
                borderColor: 'transparent',
              },
              onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
                e.currentTarget.style.backgroundColor = product.colorLight;
                e.currentTarget.style.borderColor = product.colorBorder;
              },
              onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'transparent';
              },
            };

            const cardContent = (
              <>
                {/* Icon container */}
                <div
                  className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: product.colorLight }}
                >
                  {product.icon}
                </div>

                {/* Text */}
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3
                      className="text-sm font-semibold tracking-tight"
                      style={{ color: product.color }}
                    >
                      {product.name}
                    </h3>
                    {product.external ? (
                      <svg
                        className="w-3 h-3 opacity-40 transition-all duration-300 group-hover:opacity-70"
                        style={{ color: product.color }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    ) : (
                      <svg
                        className="w-3 h-3 opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-70 group-hover:translate-x-0"
                        style={{ color: product.color }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 leading-snug truncate">
                    {product.tagline}
                  </p>
                </div>
              </>
            );

            return (
            <motion.div key={product.name} variants={itemVariants}>
              {product.external ? (
                <a
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...linkProps}
                >
                  {cardContent}
                </a>
              ) : (
                <Link to={product.link} {...linkProps}>
                  {cardContent}
                </Link>
              )}

              {/* Divider for mobile */}
              {index < products.length - 1 && (
                <div className="md:hidden h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mt-3" />
              )}
            </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default InnovationStrip;
